/* ── Style-code search (13 Oct 2026) ─────────────────────────────────────────
   Notes: mccalls-api/docs/search-style-codes-13-10-2026.md

   1. Everyone: a code-like term ("72765", "71301", "72765/00", "W12133010") is
      sent to Shopify as `tag:<term>`. That matches whole words of the style-code
      tag (`72765/00 LEWIS` → 72765, 00, LEWIS), so 72765 finds all four
      Lewis/Elson and nothing else. A plain "72765" also matched description
      text and alias barcodes (Prestige Charcoal 44S has barcode 72765).
   2. When (1) finds nothing — part of a word, "W121" for W12133010, or a
      TRIMS product number like "842" — match the code list from
      templates/collection.search-codes.liquid (13 pages of 250, ~170 KB once,
      kept 2 h). Used without asking on staff devices; everyone else sees
      "Searching for a product code?" + a button, and only that click
      downloads the list (after which it's used without asking for 2 h).
      A device is a staff device after visiting any page with ?staff=1
      (?staff=0 undoes it). It's a convenience switch, not security.
   3. Otherwise: Shopify search as typed, as before.

   Used by assets/drawers.js (search drawer) and sections/search.liquid.
   window.McCallsCodeSearch = { looksLikeCode, tagQuery, isStaff, canAutoUse, load, ready, find, searchUrl }
   ───────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  const CACHE_KEY = 'mccalls:code-index:v1';
  const STAFF_KEY = 'mccalls:staff-device';
  const CACHE_MS = 2 * 60 * 60 * 1000;
  const MIN_LEN = 3;          // "842", "W12" and up
  const MIN_CONTAINS_LEN = 4; // "contains" matches only from 4 characters, or "100" matches half the shop

  let items = null;
  let loading = null;

  // ?staff=1 / ?staff=0 on any page marks or unmarks this device.
  try {
    const flag = new URLSearchParams(location.search).get('staff');
    if (flag === '1') localStorage.setItem(STAFF_KEY, '1');
    else if (flag === '0') localStorage.removeItem(STAFF_KEY);
  } catch (e) { /* storage blocked: not a staff device */ }

  function isStaff() {
    try { return localStorage.getItem(STAFF_KEY) === '1'; } catch (e) { return false; }
  }

  // "72765/00 LEWIS" → "7276500LEWIS": slashes, spaces and dashes don't matter.
  const squash = (s) => String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

  // One word with a digit or a slash ("72765", "W121", "LAURELHILL/T/YEL").
  // Plain words and phrases ("Elson", "2 piece suit") stay with Shopify search.
  function looksLikeCode(term) {
    const t = String(term || '').trim();
    return !/\s/.test(t) && /[\d/]/.test(t) && squash(t).length >= MIN_LEN;
  }

  // Shopify field query; quotes, colons and brackets would change its meaning.
  function tagQuery(term) {
    return 'tag:' + String(term || '').trim().replace(/["():]/g, '');
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  // Full search page for a term: code-like terms go as `tag:` (the page falls
  // back to the plain term when that finds nothing).
  function searchUrl(term) {
    const t = String(term || '').trim();
    if (!t) return root() + 'search';
    return looksLikeCode(t)
      ? root() + 'search?q=' + encodeURIComponent(tagQuery(t)) + '&type=product'
      : root() + 'search?q=' + encodeURIComponent(t) + '&options%5Bprefix%5D=last';
  }

  function toItem(row) {
    return {
      code: row[0] || '',
      trims: row[1] || '',
      handle: row[2],
      title: row[3] || '',
      vendor: row[4] || '',
      image: row[5] ? (row[5].startsWith('//') ? 'https:' + row[5] : row[5]) : '',
      priceHtml: row[6] || '',
      key: squash(row[0])
    };
  }

  function readCache() {
    try {
      const c = JSON.parse(localStorage.getItem(CACHE_KEY));
      if (c && Array.isArray(c.rows) && Date.now() - c.t < CACHE_MS) return c.rows;
    } catch (e) { /* private window, blocked storage, bad JSON */ }
    return null;
  }

  function writeCache(rows) {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), rows })); } catch (e) { /* full or blocked */ }
  }

  async function fetchPage(n) {
    const res = await fetch(root() + 'collections/all?view=search-codes&page=' + n, { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Code index page ' + n + ': HTTP ' + res.status);
    return res.json();
  }

  // The list is used without asking on staff devices and on any device that
  // already holds it (someone pressed "Search product codes" in the last 2 h).
  function canAutoUse() {
    return isStaff() || items !== null || readCache() !== null;
  }

  // { optIn: true } = the visitor pressed "Search product codes". Otherwise
  // only where canAutoUse(); resolves to [] anywhere else.
  function load(opts) {
    if (!(opts && opts.optIn) && !canAutoUse()) return Promise.resolve([]);
    if (items) return Promise.resolve(items);
    if (loading) return loading;
    const cached = readCache();
    if (cached) {
      items = cached.map(toItem);
      return Promise.resolve(items);
    }
    loading = (async () => {
      const first = await fetchPage(1);
      const rest = [];
      for (let n = 2; n <= first.pages; n++) rest.push(fetchPage(n));
      const rows = [first].concat(await Promise.all(rest)).flatMap((p) => p.rows || []);
      writeCache(rows);
      items = rows.map(toItem);
      return items;
    })().catch((err) => {
      loading = null; // let the next search try again
      throw err;
    });
    return loading;
  }

  // Best first: whole code, TRIMS product number, code starts with the term,
  // code contains it. Ties: shorter code first, then A–Z.
  function find(term) {
    if (!items || !looksLikeCode(term)) return [];
    const q = squash(term);
    const raw = String(term).trim();
    const hits = [];
    for (const it of items) {
      let score = 0;
      if (it.key === q) score = 100;
      else if (it.trims && it.trims === raw) score = 90;
      else if (it.key.startsWith(q)) score = 80;
      else if (q.length >= MIN_CONTAINS_LEN && it.key.includes(q)) score = 40;
      if (score) hits.push({ item: it, score });
    }
    hits.sort((a, b) =>
      b.score - a.score ||
      a.item.key.length - b.item.key.length ||
      a.item.code.localeCompare(b.item.code) ||
      a.item.title.localeCompare(b.item.title)
    );
    return hits.map((h) => h.item);
  }

  window.McCallsCodeSearch = {
    looksLikeCode,
    tagQuery,
    isStaff,
    canAutoUse,
    load,
    ready: () => items !== null,
    find,
    searchUrl
  };

  /* ── Search page (sections/search.liquid) ──────────────────────────────
     The form sends code-like terms as `tag:`. When a `tag:` search finds
     nothing the section marks itself pending: where the code list can be used
     (canAutoUse) its matches are shown as product cards
     (sections/search-code-card.liquid); otherwise the page moves on to the
     plain search for the term, which offers "Search product codes". */
  const PAGE_MAX = 48;

  async function cardHtml(handle) {
    try {
      const res = await fetch(root() + 'products/' + encodeURIComponent(handle) + '?section_id=search-code-card', { credentials: 'same-origin' });
      if (!res.ok) return '';
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const section = doc.querySelector('[id^="shopify-section-"]');
      return section ? section.innerHTML.trim() : '';
    } catch (e) {
      return '';
    }
  }

  // Code-list matches as product cards in place of Shopify's results.
  // Resolves false when there are none (the page is left as it is).
  async function showCodeCards(page, term, opts) {
    await load(opts);
    const hits = find(term);
    const cards = (await Promise.all(hits.slice(0, PAGE_MAX).map((it) => cardHtml(it.handle)))).filter(Boolean);
    if (!cards.length) return false;

    const codeBox = page.querySelector('[data-search-code-results]');
    const grid = document.createElement('div');
    grid.className = 'search-page__grid grid grid--4';
    grid.innerHTML = cards.join('');
    codeBox.replaceChildren(grid);
    if (hits.length > cards.length) {
      const note = document.createElement('p');
      note.className = 'search-page__code-note';
      note.textContent = 'Showing the first ' + cards.length + ' of ' + hits.length + ' product code matches. Type more of the code to narrow it down.';
      codeBox.prepend(note);
    }
    codeBox.hidden = false;
    page.querySelectorAll('[data-search-shopify], [data-code-prompt]').forEach((el) => el.remove());
    const title = page.querySelector('[data-search-title]');
    if (title) title.textContent = hits.length + (hits.length === 1 ? ' result' : ' results') + ' for "' + term + '"';
    return true;
  }

  // "Searching for a product code?" above Shopify's results for a code-like term.
  function addCodePrompt(page, term) {
    const shopifyBox = page.querySelector('[data-search-shopify]');
    if (!shopifyBox) return;
    const box = document.createElement('div');
    box.className = 'search-page__code-prompt';
    box.setAttribute('data-code-prompt', '');
    const text = document.createElement('p');
    text.className = 'search-page__code-prompt-text';
    text.textContent = 'Searching for a product code?';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'search-page__code-button';
    btn.textContent = 'Search product codes for \u201c' + term + '\u201d';
    box.append(text, btn);
    shopifyBox.before(box);

    btn.addEventListener('click', async () => {
      btn.disabled = true;
      btn.textContent = 'Searching product codes\u2026';
      try {
        if (await showCodeCards(page, term, { optIn: true })) return;
        text.textContent = 'No product codes start with or contain \u201c' + term + '\u201d.';
        btn.remove();
      } catch (err) {
        console.warn('Product code list failed', err);
        btn.disabled = false;
        btn.textContent = 'Couldn\u2019t load product codes \u2014 try again';
      }
    });
  }

  async function initSearchPage() {
    const page = document.querySelector('[data-search-page]');
    if (!page) return;

    const form = page.querySelector('form[role="search"]');
    if (form) {
      form.addEventListener('submit', (e) => {
        const input = form.querySelector('input[name="q"]');
        if (!input || !looksLikeCode(input.value)) return;
        e.preventDefault();
        location.href = searchUrl(input.value);
      });
    }

    const term = page.getAttribute('data-terms') || '';
    const pending = page.querySelector('[data-search-shopify].is-code-pending');
    if (!pending) {
      if (looksLikeCode(term)) addCodePrompt(page, term);
      return;
    }

    const plain = () => location.replace(root() + 'search?q=' + encodeURIComponent(term) + '&options%5Bprefix%5D=last');
    if (!canAutoUse()) return plain();
    try {
      if (!(await showCodeCards(page, term))) plain();
    } catch (err) {
      console.warn('Product code list failed; showing Shopify search', err);
      plain();
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initSearchPage);
  else initSearchPage();
})();
